import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from '@phosphor-icons/react';
import { useLanguage } from '@/i18n/LanguageContext';

const typingTextsEn = [
  "Design a robotic assembly arm...",
  "Create an autonomous drone chassis...",
  "Generate a smart IoT sensor enclosure...",
];

const typingTextsTr = [
  "Robotik montaj kolu tasarla...",
  "Otonom drone şasisi oluştur...",
  "Akıllı IoT sensör muhafazası üret...",
];

export function HeroSection() {
  const { language } = useLanguage();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  const typingTexts = language === 'tr' ? typingTextsTr : typingTextsEn;

  useEffect(() => {
    setDisplayText('');
    setCurrentTextIndex(0);
    setIsTyping(true);
  }, [language]);

  useEffect(() => {
    const text = typingTexts[currentTextIndex];
    if (isTyping) {
      if (displayText.length < text.length) {
        const timeout = setTimeout(() => {
          setDisplayText(text.slice(0, displayText.length + 1));
        }, 60);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => setIsTyping(false), 2500);
        return () => clearTimeout(timeout);
      }
    } else {
      if (displayText.length > 0) {
        const timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 25);
        return () => clearTimeout(timeout);
      } else {
        setCurrentTextIndex((prev) => (prev + 1) % typingTexts.length);
        setIsTyping(true);
      }
    }
  }, [displayText, isTyping, currentTextIndex, typingTexts]);

  const scrollToDemo = () => {
    document.getElementById('studio')?.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = [
    { value: 'Beta', label: language === 'tr' ? 'Aşaması — Erken Erişim' : 'Stage — Early Access' },
    { value: 'TBD', label: language === 'tr' ? 'Pilot Ölçümü Bekleniyor' : 'Pilot Measurement Pending' },
    { value: language === 'tr' ? 'İzlenebilir' : 'Traceable', label: language === 'tr' ? 'Doğrulama Kapıları' : 'Validation Gates' },
  ];

  return (
    <section 
      className="relative min-h-screen flex items-center bg-[#060710] overflow-hidden"
      data-testid="hero-section"
    >
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-[0.04]">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Vibrant ambient glows — richer color, more presence */}
      <div className="pointer-events-none absolute -top-40 -left-20 w-[700px] h-[700px] bg-[#4F9DFF] opacity-[0.14] blur-[160px] rounded-full" />
      <div className="pointer-events-none absolute top-1/4 -right-32 w-[800px] h-[800px] bg-[#9B6BFF] opacity-[0.12] blur-[180px] rounded-full" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 w-[600px] h-[600px] bg-[#22D3EE] opacity-[0.08] blur-[160px] rounded-full" />
      <div className="pointer-events-none absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#FFB547] opacity-[0.04] blur-[140px] rounded-full" />

      {/* Noise texture overlay */}
      <div className="absolute inset-0 opacity-[0.02] bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')]" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left content - spans 7 columns */}
          <div className="lg:col-span-7 space-y-8">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 rounded-full text-xs font-mono uppercase tracking-wider text-white/60">
                <span className="w-1.5 h-1.5 bg-[#FF3B30] rounded-full animate-pulse" />
                {language === 'tr' ? 'AI-Destekli Tasarım' : 'AI-Powered Design'}
              </span>
            </motion.div>

            {/* Main heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.1]"
            >
              {language === 'tr' ? (
                <>
                  Fikirden <span className="font-bold bg-gradient-to-r from-cyan-300 via-blue-300 to-violet-300 bg-clip-text text-transparent">İncelenebilir</span>{' '}
                  <br />
                  <span className="text-white/70">Mühendislik Briefine</span>
                </>
              ) : (
                <>
                  From Idea to a <span className="font-bold bg-gradient-to-r from-cyan-300 via-blue-300 to-violet-300 bg-clip-text text-transparent">Reviewable</span>{' '}
                  <br />
                  <span className="text-white/70">Engineering Brief</span>
                </>
              )}
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg text-white/70 max-w-xl leading-relaxed"
            >
              {language === 'tr' 
                ? 'Endüstriyel ürün fikirlerini AI destekli analizle yapılandırılmış gereksinimlere, ön mühendislik çıktılarına ve CAD\'e hazır iş akışlarına dönüştürün.'
                : 'Turn industrial product ideas into structured requirements, preliminary engineering outputs and CAD workflow ready for reviews with AI-assisted analysis.'}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <button
                onClick={scrollToDemo}
                className="group flex items-center gap-3 px-6 py-3.5 bg-white text-black font-medium rounded-none hover:bg-white/90 transition-colors"
                data-testid="hero-try-demo-btn"
              >
                <Play weight="fill" className="w-4 h-4" />
                {language === 'tr' ? 'Demo Dene' : 'Try Demo'}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              
              <a
                href="/pricing"
                className="flex items-center gap-2 px-6 py-3.5 border border-white/20 text-white/80 font-medium rounded-none hover:bg-white/5 hover:border-white/30 transition-all"
                data-testid="hero-pricing-btn"
              >
                {language === 'tr' ? 'Fiyatlandırma' : 'View Pricing'}
              </a>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex gap-8 pt-8 border-t border-white/10"
            >
              {stats.map((stat, index) => (
                <div key={index} className="space-y-1">
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-white/60 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right content - terminal mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-[#FF3B30]/20 to-transparent blur-3xl opacity-30" />
              
              {/* Terminal window */}
              <div className="relative bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden">
                {/* Terminal header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#121212]">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#27C93F]" />
                  </div>
                  <span className="ml-2 text-xs text-white/30 font-mono">nexus-ai · {language === 'tr' ? 'örnek iş akışı' : 'illustrative workflow'}</span>
                </div>
                
                {/* Terminal content */}
                <div className="p-6 min-h-[280px]">
                  <div className="font-mono text-sm space-y-3">
                    <div className="flex items-start gap-2">
                      <span className="text-[#FF3B30]">$</span>
                      <span className="text-white/70">nexus design --prompt</span>
                    </div>
                    <div className="pl-4 text-white/50 min-h-[24px]">
                      "{displayText}<span className="animate-pulse">|</span>"
                    </div>
                    <div className="pt-4 space-y-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-[#27C93F]">✓</span>
                        <span className="text-white/40">{language === 'tr' ? 'AI analizi tamamlandı' : 'AI analysis complete'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#27C93F]">✓</span>
                        <span className="text-white/40">{language === 'tr' ? '3D konsept uygunluğu kontrol edildi' : '3D concept availability checked'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#27C93F]">✓</span>
                        <span className="text-white/40">{language === 'tr' ? 'CAD dışa aktarım uygunluğu kontrol edildi' : 'CAD export eligibility checked'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-5 h-8 border border-white/20 rounded-full flex justify-center pt-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1 h-1 bg-white/40 rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
}

export default HeroSection;
